import React, { useState } from 'react';
import { AlertCircle, CheckCircle2, ArrowRightCircle } from 'lucide-react';

export default function RegulatoryConfirmationModal({ show, onClose, onConfirm, reservation, beds = [] }) {
    const [reason, setReason] = useState('');
    const [allocationType, setAllocationType] = useState('sector'); // 'sector' or 'specific'
    const [observation, setObservation] = useState('');
    const [showHelp, setShowHelp] = useState(false); // [Novo] Controle do Help
    const [selectedBedId, setSelectedBedId] = useState(null); // [Novo]
    const [loading, setLoading] = useState(false);

    if (!show || !reservation) return null;

    // Filtrar leitos do setor da reserva que estão VAGOS ou HIGIENIZADOS
    // Filtrar leitos do setor da reserva que estão VAGOS ou HIGIENIZADOS
    const availableBeds = beds.filter(b =>
        b.sectorId == reservation.sector_id && // [Fix] Model returned camelCase 'sectorId'
        (b.statusId === 1 || b.statusId === 9) // 1=Livre, 9=Higienizado (Pronto)
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        // Passar dados estruturados para o pai
        await onConfirm({
            reservationId: reservation.id,
            reason,
            allocationType,
            observation,
            specificBedId: allocationType === 'specific' ? selectedBedId : null // [Novo]
        });
        setLoading(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-800 flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${reservation.type === 'transfer' ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                            {reservation.type === 'transfer' ? <ArrowRightCircle size={24} /> : <CheckCircle2 size={24} />}
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                {reservation.type === 'transfer' ? 'Confirmar Transferência' : 'Confirmar Reserva'}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {reservation.type === 'transfer' ? 'Defina o destino do paciente' : 'Validação regulatória obrigatória'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto">

                    {/* Resumo Rápido */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/50">
                        <div className="text-sm text-blue-800 dark:text-blue-300 grid grid-cols-2 gap-2">
                            <div><strong>Origem:</strong> {reservation.origin}</div>
                            <div><strong>Prioridade:</strong> {reservation.priority}</div>
                            <div className="col-span-2"><strong>Paciente:</strong> {reservation.patient_gender}, {reservation.patient_condition}</div>
                        </div>
                    </div>

                    {/* Motivo Regulatório */}
                    <div>
                        <div className="flex justify-between items-center mb-3">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                Motivo da Regulação <span className="text-red-500">*</span>
                            </label>
                            <button
                                type="button"
                                onClick={() => setShowHelp(!showHelp)}
                                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 flex items-center gap-1 transition-colors"
                            >
                                <AlertCircle size={14} />
                                {showHelp ? 'Ocultar Explicação' : 'Entenda os Motivos'}
                            </button>
                        </div>

                        {/* Help Box (Condicional) */}
                        {showHelp && (
                            <div className="mb-4 bg-blue-50 dark:bg-blue-900/10 p-4 rounded-lg border border-blue-100 dark:border-blue-800 text-sm animate-in fade-in slide-in-from-top-2">
                                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                                    <li>🚑 <strong>Admissão via PS:</strong> Paciente já está na emergência e precisa subir.</li>
                                    <li>🏥 <strong>Transf. Externa:</strong> Paciente vindo de outra unidade (UPA/Outro Hospital).</li>
                                    <li>🔄 <strong>Transf. Interna:</strong> Troca de setor (Ex: UTI &rarr; Enfermaria).</li>
                                    <li>🛌 <strong>Retaguarda (Giro):</strong> Manobra estratégica para liberar vagas críticas.</li>
                                </ul>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-2">
                            {[
                                'Admissão via Pronto-Socorro',
                                'Transferência Externa',
                                'Transferência Interna (Setor)',
                                'Retaguarda Clínica (Giro de Leito)'
                            ].map((opt) => (
                                <label key={opt} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50/50 dark:has-[:checked]:bg-blue-900/20">
                                    <input
                                        type="radio"
                                        name="regulationReason"
                                        value={opt}
                                        checked={reason === opt}
                                        onChange={(e) => setReason(e.target.value)}
                                        required
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Tipo de Alocação */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Tipo de Alocação
                        </label>
                        <div className="flex flex-col gap-3">
                            <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                <input
                                    type="radio"
                                    name="allocType"
                                    value="sector"
                                    checked={allocationType === 'sector'}
                                    onChange={(e) => setAllocationType(e.target.value)}
                                    className="text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                    <span className="text-sm font-medium text-gray-900 dark:text-white block">Vaga no Setor (Automático)</span>
                                    <span className="text-xs text-gray-500">O sistema alertará quando qualquer leito do setor liberar.</span>
                                </div>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                <input
                                    type="radio"
                                    name="allocType"
                                    value="specific"
                                    checked={allocationType === 'specific'}
                                    onChange={(e) => setAllocationType(e.target.value)}
                                    className="text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                    <span className="text-sm font-medium text-gray-900 dark:text-white block">Definir Leito Específico</span>
                                    <span className="text-xs text-gray-500">Já alocar o paciente em um leito vago agora.</span>
                                </div>
                            </label>

                            {/* Dropdown de Leitos (Só aparece se Specific for selecionado) */}
                            {allocationType === 'specific' && (
                                <div className="ml-8 animate-in fade-in slide-in-from-top-2">
                                    <select
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                        onChange={(e) => setSelectedBedId(e.target.value)}
                                        required={allocationType === 'specific'}
                                    >
                                        <option value="">Selecione um leito disponível...</option>
                                        {availableBeds.length > 0 ? (
                                            availableBeds.map(bed => (
                                                <option key={bed.id} value={bed.id}>
                                                    {bed.number} - {bed.statusId === 1 ? 'Disponível' : 'Higienizado'}
                                                </option>
                                            ))
                                        ) : (
                                            <option disabled>Nenhum leito disponível neste setor.</option>
                                        )}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Observação */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Observação (Opcional)
                        </label>
                        <textarea
                            value={observation}
                            onChange={(e) => setObservation(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-black/20 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            rows="2"
                            placeholder="Detalhes adicionais..."
                        ></textarea>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-2"
                        >
                            {loading ? 'Processando...' : 'Confirmar Reserva'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
